// KPI visibility (KPI-VISIBILITY, founder-approved 2026-10-22; 05 §KPI visibility): which seats'
// KPI data a person may read. The floor (seats, holders, titles, teams, reporting lines,
// responsibilities) is public and never passes through here; this decides the DATA on a seat —
// its scorecard and KPIs, its priorities, its task load and its money — per dimension group.
//
//   - Everyone reads their own seats, and anyone with reports reads their whole branch below
//     (the default: "a department manager sees themself and everyone underneath them").
//   - A grant widens that for a principal — a job role, a seat, a team or a person — by scopes:
//     `own`, `branch` (down), `peers` (the seats sharing a parent, or the job role, with one of
//     the principal's seats), `up` (the chain above) and `everything`. Grants are additive.
//   - A grant applies to some dimension groups (`kpi`, `priorities`, `tasks`, `finance`) or, with
//     none named, to all of them.
//   - Company managers (owner, admin, agency operator) read everything, always.
// The resolver is pure: the API and the fake twin hand it the seat tree and the grants and get
// back the seat ids per group. A scope is anchored on the seats through which the grant reaches
// the person (the seats of the role, the seat itself, the team's seats the person holds); a
// person grant anchors on every seat they hold.

export type KpiScope = "own" | "branch" | "peers" | "up" | "everything";
export const KPI_SCOPES: readonly KpiScope[] = ["own", "branch", "peers", "up", "everything"];

export type KpiGroup = "kpi" | "priorities" | "tasks" | "finance";
export const KPI_GROUPS: readonly KpiGroup[] = ["kpi", "priorities", "tasks", "finance"];

export type KpiPrincipalKind = "role" | "seat" | "team" | "person";
export const KPI_PRINCIPAL_KINDS: readonly KpiPrincipalKind[] = ["role", "seat", "team", "person"];

export type KpiGrant = {
  id: string;
  principal_kind: KpiPrincipalKind;
  principal_id: string;
  scopes: readonly KpiScope[];
  /** The dimension groups it applies to; empty: every group ("all KPI dimensions"). */
  groups: readonly KpiGroup[];
};

export type KpiTreeSeat = {
  id: string;
  supervisor_seat_id: string | null;
  role_id: string | null;
  /** The memberships holding it now. */
  holders: readonly string[];
};

export type KpiTree = {
  seats: readonly KpiTreeSeat[];
  /** Team id → its member seats (lead and human-owner seats count as the team's). */
  teams: ReadonlyMap<string, readonly string[]>;
};

export type KpiViewer = { membership_id: string; manager: boolean };

/** Per group: every seat (`all`), or the seat ids. */
export type KpiVisibility = Record<KpiGroup, { all: boolean; seats: Set<string> }>;

/** The seats a grant can be anchored on for a person: the seats of theirs it names. */
export function grantAnchors(grant: KpiGrant, tree: KpiTree, held: ReadonlySet<string>): string[] {
  const mine = tree.seats.filter((s) => held.has(s.id));
  switch (grant.principal_kind) {
    case "person":
      return mine.map((s) => s.id);
    case "seat":
      return mine.filter((s) => s.id === grant.principal_id).map((s) => s.id);
    case "role":
      return mine.filter((s) => s.role_id === grant.principal_id).map((s) => s.id);
    case "team": {
      const members = new Set(tree.teams.get(grant.principal_id) ?? []);
      return mine.filter((s) => members.has(s.id)).map((s) => s.id);
    }
  }
}

function indexOf(tree: KpiTree) {
  const sup = new Map(tree.seats.map((s) => [s.id, s.supervisor_seat_id]));
  const children = new Map<string, string[]>();
  for (const s of tree.seats) {
    const p = s.supervisor_seat_id;
    if (p === null || p === s.id) continue;
    const list = children.get(p) ?? [];
    list.push(s.id);
    children.set(p, list);
  }
  return { sup, children };
}

type Index = ReturnType<typeof indexOf>;

/** The seat and every seat below it (cycle-safe). */
export function branchOf(seatId: string, children: ReadonlyMap<string, readonly string[]>) {
  const out = new Set<string>([seatId]);
  const stack = [seatId];
  while (stack.length > 0) {
    const cur = stack.pop() as string;
    for (const c of children.get(cur) ?? [])
      if (!out.has(c)) {
        out.add(c);
        stack.push(c);
      }
  }
  return out;
}

/** The seat and its supervisor chain (cycle-safe). */
export function chainOf(seatId: string, sup: ReadonlyMap<string, string | null>) {
  const out = new Set<string>([seatId]);
  let cur = sup.get(seatId) ?? null;
  while (cur && !out.has(cur)) {
    out.add(cur);
    cur = sup.get(cur) ?? null;
  }
  return out;
}

/** The seats on an anchor's level: the same parent (top seats share none), or the same job role. */
export function peersOf(seatId: string, tree: KpiTree, idx: Index) {
  const me = tree.seats.find((s) => s.id === seatId);
  const out = new Set<string>([seatId]);
  if (!me) return out;
  const parent = idx.sup.get(seatId) ?? null;
  for (const s of tree.seats) {
    if (parent !== null && s.supervisor_seat_id === parent) out.add(s.id);
    else if (parent === null && s.supervisor_seat_id === null) out.add(s.id);
    if (me.role_id !== null && s.role_id === me.role_id) out.add(s.id);
  }
  return out;
}

function scopeSeats(scope: KpiScope, anchor: string, tree: KpiTree, idx: Index): Set<string> {
  switch (scope) {
    case "own":
      return new Set([anchor]);
    case "branch":
      return branchOf(anchor, idx.children);
    case "peers":
      return peersOf(anchor, tree, idx);
    case "up":
      return chainOf(anchor, idx.sup);
    case "everything":
      return new Set(tree.seats.map((s) => s.id));
  }
}

/** The groups a grant covers. */
export const grantGroups = (g: Pick<KpiGrant, "groups">): readonly KpiGroup[] =>
  g.groups.length === 0 ? KPI_GROUPS : g.groups;

/** The default (no grant): own seats and the branch below each. */
export function defaultKpiSeats(tree: KpiTree, membershipId: string): Set<string> {
  const idx = indexOf(tree);
  const out = new Set<string>();
  for (const s of tree.seats)
    if (s.holders.includes(membershipId))
      for (const id of branchOf(s.id, idx.children)) out.add(id);
  return out;
}

/**
 * The seats whose KPI data `viewer` may read, per group: the default (own and branch) plus every
 * grant that names them; everything for a company manager or an `everything` scope.
 */
export function resolveKpiVisibility(
  tree: KpiTree,
  viewer: KpiViewer,
  grants: readonly KpiGrant[],
): KpiVisibility {
  const out = grantedKpiVisibility(tree, viewer, grants);
  const base = defaultKpiSeats(tree, viewer.membership_id);
  for (const g of KPI_GROUPS) if (!out[g].all) for (const id of base) out[g].seats.add(id);
  return out;
}

/**
 * What the grants alone open for `viewer` (the default left out): the reads that keep an audience
 * level of their own (the chart's gated dimensions, the Keystones lens) add this to their level.
 */
export function grantedKpiVisibility(
  tree: KpiTree,
  viewer: KpiViewer,
  grants: readonly KpiGrant[],
): KpiVisibility {
  const out = {} as KpiVisibility;
  for (const g of KPI_GROUPS) out[g] = { all: viewer.manager, seats: new Set() };
  if (viewer.manager) {
    for (const g of KPI_GROUPS) out[g].seats = new Set(tree.seats.map((s) => s.id));
    return out;
  }
  const idx = indexOf(tree);
  const held = new Set(
    tree.seats.filter((s) => s.holders.includes(viewer.membership_id)).map((s) => s.id),
  );
  for (const grant of grants) {
    const groups = grantGroups(grant);
    // An `everything` scope needs no anchor: a person grant reaches someone with no seat too.
    const named =
      grant.principal_kind === "person" ? grant.principal_id === viewer.membership_id : null;
    if (grant.scopes.includes("everything") && (named ?? grantAnchors(grant, tree, held).length)) {
      for (const g of groups) {
        out[g].all = true;
        out[g].seats = new Set(tree.seats.map((s) => s.id));
      }
      continue;
    }
    if (named === false) continue;
    const anchors = grantAnchors(grant, tree, held);
    for (const anchor of anchors)
      for (const scope of grant.scopes)
        for (const id of scopeSeats(scope, anchor, tree, idx))
          for (const g of groups) if (!out[g].all) out[g].seats.add(id);
  }
  return out;
}

/** Whether a group's visibility reaches a seat. */
export const seesKpi = (v: KpiVisibility, group: KpiGroup, seatId: string) =>
  v[group].all || v[group].seats.has(seatId);

/** The chart's gated dimensions, by the group that governs them. */
export const KPI_GROUP_OF = {
  kpi: "kpi",
  overall: "kpi",
  keystones: "priorities",
  load: "tasks",
  cost: "finance",
} as const satisfies Record<string, KpiGroup>;

const SCOPE_TEXT: Record<KpiScope, string> = {
  own: "Own seat",
  branch: "My branch",
  peers: "Same level",
  up: "Up my chain",
  everything: "Everything",
};
const GROUP_TEXT: Record<KpiGroup, string> = {
  kpi: "KPIs",
  priorities: "priorities",
  tasks: "tasks",
  finance: "finance",
};

/** A grant's scopes and groups in words: "Same level + Up my chain, all KPI dimensions". */
export function describeGrant(g: Pick<KpiGrant, "scopes" | "groups">): string {
  const scopes = KPI_SCOPES.filter((s) => g.scopes.includes(s))
    .map((s) => SCOPE_TEXT[s])
    .join(" + ");
  const groups =
    g.groups.length === 0 || g.groups.length === KPI_GROUPS.length
      ? "all KPI dimensions"
      : KPI_GROUPS.filter((x) => g.groups.includes(x))
          .map((x) => GROUP_TEXT[x])
          .join(", ");
  return `${scopes}, ${groups}`;
}

export const KPI_SCOPE_LABELS = SCOPE_TEXT;
export const KPI_GROUP_LABELS: Record<KpiGroup, string> = {
  kpi: "KPIs & performance",
  priorities: "Priorities",
  tasks: "Tasks & load",
  finance: "Finance",
};
